import Component from '@glimmer/component'
import { ManageHeading } from 'district-ui-client/components/manage-heading'
import { RolloverAlert } from 'district-ui-client/components/rollover-alert'
import { TeacherTable } from 'district-ui-client/components/teacher-table/component'
import { t, type IntlService } from 'ember-intl'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import { UiButton } from 'district-ui-client/components/ui-button'
import ActionMenu from 'district-ui-client/components/base/action-menu'
import ActionMenuItem from 'district-ui-client/components/base/action-menu/item'
import { on } from '@ember/modifier'
import UiButtonLinkTo from 'district-ui-client/components/ui-button/link-to'
import { ModalDefault } from 'district-ui-client/components/modal-default'
import { fn } from '@ember/helper'
import { service } from '@ember/service'
import type FlashQueueService from 'district-ui-client/services/flash-queue'
import type TeacherOperationsService from 'district-ui-client/services/teacher-operations'
import type { Log } from '@blakeelearning/log'
import type ActiveRouteService from 'district-ui-client/services/active-route'
import type Store from '@ember-data/store'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import { isEmpty } from '@ember/utils'
import { tracked } from '@glimmer/tracking'
import type School from 'district-ui-client/models/school'
import type ManageTeachersIndexRoute from 'district-ui-client/subscription-type/manage/teachers/index/route'
import RouteTemplate from 'ember-route-template'
import type ManageTeachersIndexController from 'district-ui-client/subscription-type/manage/teachers/index/controller'
import { hash } from '@ember/helper'
import type ExternalRosteringService from 'district-ui-client/services/external-rostering'
import type FeaturesService from 'district-ui-client/services/features'
import PhosphorIcon from 'district-ui-client/components/phosphor-icon'
import { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import { RosterRedirectPanel } from 'district-ui-client/components/roster/redirect-panel'

interface Signature {
  Args: {
    model: ModelFor<ManageTeachersIndexRoute>
    controller: ManageTeachersIndexController
  }
}

export class ManageTeachersIndexRouteTemplate extends Component<Signature> {
  @service flashQueue!: FlashQueueService

  @service teacherOperations!: TeacherOperationsService

  @service intl!: IntlService

  @service log!: Log

  @service activeRoute!: ActiveRouteService

  @service authToken!: AuthToken

  @service store!: Store

  @service externalRostering!: ExternalRosteringService

  @service features!: FeaturesService

  @tracked selectedIds: string[] = []

  @tracked showChangeSchoolModal = false

  get showRosterRedirect() {
    return this.features.isEnabled('dui_rostering_improvements_2026') && this.externalRostering.rostersInRosterApp
  }

  get readOnly() {
    return this.is3EEnabled || this.externalRostering.isEnabled || this.subscriptionType === SubscriptionType.Mathletics
  }

  get is3EEnabled() {
    return this.args.model.has3ESchool
  }

  get createTeacherDisabled() {
    return this.readOnly
  }

  get groupActionsDisabled() {
    return isEmpty(this.selectedIds) || this.readOnly
  }

  get subscriptionType() {
    return this.activeRoute.subscriptionType
  }

  setChangeSchoolModal = (value: boolean) => {
    this.showChangeSchoolModal = value
  }

  updateSelectedIds = (selectedIds: string[]) => {
    this.selectedIds = selectedIds
  }

  changeSchool = async (school: School) => {
    const teachers = this.selectedTeachers

    const response = await this.teacherOperations.moveTeachersToSchool(teachers, school, this.subscriptionType)

    if (response.ok) {
      this.uncheckSelectedItems()
      this.flashQueue.addSuccess({
        subtitle: `Successfully moved ${teachers.length} teachers`,
      })
    } else {
      this.flashQueue.addCritical({
        subtitle: 'Failed to move teachers',
      })
    }

    this.setChangeSchoolModal(false)
  }

  uncheckSelectedItems() {
    this.selectedIds = []
  }

  get selectedTeachers() {
    return this.args.controller.teacherQueryResult.filter((t) => this.selectedIds.includes(t.id))
  }

  loginDetails = async () => {
    const teachers = this.selectedTeachers
    if (teachers.length > 0) {
      const response = await this.teacherOperations.sendLoginDetails(teachers, this.subscriptionType)
      if (response.ok) {
        this.uncheckSelectedItems()
        this.flashQueue.addSuccess({ subtitle: this.intl.t('subscriptionType.manageTeachers.sendLoginDetailsSuccess') })
      } else {
        this.log.error('failed to send login details', await response.text())
        this.flashQueue.addCritical({
          subtitle: this.intl.t('errorMessages.loginDetails'),
        })
      }
    }
  }

  deleteTeachers = async () => {
    const teachers = this.selectedTeachers
    if (teachers.length > 0) {
      const confirmMessage = `Are you sure you wish to delete ${teachers.length} teacher(s)`
      const confirmResponse = window.confirm(confirmMessage)
      if (!confirmResponse) return

      const successMessage = `Successfully deleted ${teachers.length} teachers`

      try {
        const promises = teachers.map((teacher) => teacher.destroyRecord({ adapterOptions: { bulk: true } }))
        await Promise.all(promises)

        this.flashQueue.addSuccess({ subtitle: successMessage })
        this.uncheckSelectedItems()
      } catch (e: any) {
        teachers.forEach((teacher) => teacher.rollbackAttributes())

        this.log.error('failed to delete teachers', e)
        this.flashQueue.addCritical({
          subtitle: this.intl.t('errorMessages.deleteTeachers'),
        })
      }
    }
  }

  passwordReset = async () => {
    const teachers = this.selectedTeachers
    if (teachers.length > 0) {
      const response = await this.teacherOperations.passwordReset(teachers)
      if (response.ok) {
        this.uncheckSelectedItems()
        this.flashQueue.addSuccess({ subtitle: this.intl.t('subscriptionType.manageTeachers.passwordResetSuccess') })
      } else {
        this.log.error('failed to request password reset', await response.text())
        this.flashQueue.addCritical({
          subtitle: this.intl.t('errorMessages.passwordReset'),
        })
      }
    }
  }

  exportTeachers = async () => {
    const school = this.args.model.schoolsForSubType.find(
      (school: School) => school.id === this.args.controller.schoolId,
    )

    const response = await this.teacherOperations.exportTeachers(this.subscriptionType, school)
    if (response.ok) {
      this.flashQueue.addSuccess({
        subtitle: this.intl.t('subscriptionType.manageTeachers.exportTeachers.exportCsvSuccess'),
      })
    } else {
      this.log.error('failed to export teachers', await response.text())
      this.flashQueue.addCritical({
        subtitle: this.intl.t('subscriptionType.manageTeachers.exportTeachers.exportCsvError'),
      })
    }
  }

  <template>
    <ManageHeading>{{t "subscriptionType.manageTeachers.index.manageTeachers"}}</ManageHeading>
    {{#if this.showRosterRedirect}}
      <RosterRedirectPanel
        class="mt-6"
        @schools={{@model.schoolsForSubType}}
        @destination="manage-teachers"
        @program={{this.subscriptionType}}
      />
    {{else}}
      <RolloverAlert />

      <div class="mt-6">
        <TeacherTable
          @teachers={{@controller.teacherQueryResult}}
          @selectedIds={{this.selectedIds}}
          @updateSelectedIds={{this.updateSelectedIds}}
          @subscriptionType={{this.subscriptionType}}
          @sortedSchoolsOptions={{@controller.sortedSchoolsOptions}}
          @selectSchool={{@controller.selectSchool}}
          @selectedSchoolOption={{@controller.selectedSchoolOption}}
          @isTeachersPending={{@controller.teacherQueryPending}}
          @disableEditing={{this.readOnly}}
        >

          <div class="mx-3 mb-3 inline-block align-middle">
            <PopoverStandardDropdown @disabled={{this.groupActionsDisabled}} as |dropdown status actions|>
              <UiButton
                data-test-bulk-actions
                class="muted"
                @corners="rounded"
                aria-haspopup="menu"
                disabled={{this.groupActionsDisabled}}
                {{dropdown.makeTrigger}}
              >{{t "subscriptionType.manageTeachers.index.groupActions"}}<PhosphorIcon
                  @icon="phosphor-caret-down-fill"
                  class="ml-1"
                />
              </UiButton>

              <dropdown.content>
                <ActionMenu data-test-actions-menu>
                  <ActionMenuItem
                    id="change-school-button"
                    {{on "click" (fn this.setChangeSchoolModal true)}}
                    {{on "click" actions.close}}
                  >
                    <PhosphorIcon @icon="phosphor-bank-fill" />
                    {{t "subscriptionType.manageTeachers.index.changeSchool"}}
                  </ActionMenuItem>
                  <ActionMenuItem
                    id="delete-teachers-button"
                    {{on "click" this.deleteTeachers}}
                    {{on "click" actions.close}}
                  >
                    <PhosphorIcon @icon="phosphor-trash-fill" />
                    {{t "subscriptionType.manageTeachers.index.delete"}}
                  </ActionMenuItem>
                  <ActionMenuItem {{on "click" this.passwordReset}} {{on "click" actions.close}}>
                    <button type="button">
                      <PhosphorIcon @icon="phosphor-lock-fill" />
                      {{t "subscriptionType.manageTeachers.index.resetPassword"}}
                    </button>
                  </ActionMenuItem>

                  <ActionMenuItem {{on "click" this.loginDetails}} {{on "click" actions.close}}>
                    <PhosphorIcon @icon="phosphor-sign-in-fill" />
                    {{t "subscriptionType.manageTeachers.index.loginDetails"}}
                  </ActionMenuItem>
                </ActionMenu>
              </dropdown.content>
            </PopoverStandardDropdown>
          </div>

          <div class="mx-3 mb-3 inline-flex justify-center align-middle">
            <UiButtonLinkTo
              @route="subscription-type.manage.teachers.new"
              @query={{hash schoolId=@controller.schoolId}}
              class="muted"
              @corners="rounded rounded-r-none"
              @disabled={{this.createTeacherDisabled}}
              data-test-add-teacher-link
            >
              <span class="flex items-center justify-center">
                <PhosphorIcon @icon="phosphor-user-fill" class="mr-1" />{{t
                  "subscriptionType.manageTeachers.index.addSingleTeacher"
                }}
              </span>
            </UiButtonLinkTo>

            <UiButtonLinkTo
              @route="subscription-type.manage.teachers.add-existing-teachers"
              class="muted"
              @corners="rounded-none"
              @disabled={{this.createTeacherDisabled}}
            >
              <span class="flex items-center justify-center">
                <PhosphorIcon @icon="phosphor-users-three-fill" class="mr-1" />{{t
                  "subscriptionType.manageTeachers.index.addExistingTeachers"
                }}</span></UiButtonLinkTo>

            <UiButton
              class="muted"
              @corners="rounded rounded-l-none"
              @disabled={{this.externalRostering.hidesTeacherLogin}}
              {{on "click" this.exportTeachers}}
              data-test-export-teachers-button
            >
              <span class="flex items-center justify-center">
                <PhosphorIcon @icon="phosphor-download-fill" class="mr-1" />{{t
                  "subscriptionType.manageTeachers.index.exportTeachers"
                }}</span></UiButton>
          </div>
        </TeacherTable>
      </div>

      {{#if this.showChangeSchoolModal}}
        <ModalDefault @onClose={{fn this.setChangeSchoolModal false}} as |modal|>
          <modal.header>
            {{t "subscriptionType.manageTeachers.index.changeSchoolModalTitle"}}
          </modal.header>
          <modal.body class="max-h-1/2-screen">
            <div class="stacked-buttons">
              {{#each @model.schoolsForSubType as |school|}}
                <UiButton
                  class="muted"
                  {{on "click" (fn this.changeSchool school)}}
                  data-test-change-school-for-teacher
                >
                  {{school.name}}
                </UiButton>
              {{/each}}
            </div>
          </modal.body>
        </ModalDefault>
      {{/if}}
    {{/if}}
  </template>
}

export default RouteTemplate(ManageTeachersIndexRouteTemplate)
